/**
 * Date helpers for the guest portal.
 *
 * Booking dates (check_in / check_out, cleaning dates) are plain calendar days
 * stored as "YYYY-MM-DD". `new Date('2026-03-15')` parses that as UTC midnight,
 * which for a guest west of UTC (the Americas) is the evening of the 14th —
 * dates showed one day early and the arrival countdown was off by one.
 * These helpers treat a bare date as a LOCAL calendar day.
 */

const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;
const DAY_MS = 24 * 60 * 60 * 1000;

/** Parse "YYYY-MM-DD" as a local calendar day; anything else via Date. */
export function parseLocalDate(value: string | null | undefined): Date | null {
  if (!value) return null;
  const m = DATE_ONLY.exec(value.slice(0, 10));
  // A full timestamp ("2026-03-15T10:00:00Z") keeps its real instant.
  if (m && value.length === 10) {
    return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  }
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** A Date's LOCAL calendar day as "YYYY-MM-DD" (toISOString() would give the UTC day). */
export function localDateKey(d: Date = new Date()): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Local midnight of the given day. */
function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/** Whole calendar days from today (local) to `value`. Negative = in the past. */
export function daysFromToday(value: string | null | undefined, now: Date = new Date()): number | null {
  const d = parseLocalDate(value);
  if (!d) return null;
  return Math.round((startOfDay(d).getTime() - startOfDay(now).getTime()) / DAY_MS);
}

/** Number of nights between two booking dates (DST-safe). */
export function nightsBetween(checkIn: string | null | undefined, checkOut: string | null | undefined): number {
  const a = parseLocalDate(checkIn);
  const b = parseLocalDate(checkOut);
  if (!a || !b) return 0;
  return Math.max(0, Math.round((startOfDay(b).getTime() - startOfDay(a).getTime()) / DAY_MS));
}

/** BCP-47 locale for the guest's UI language (keeps hr/en formats stable). */
export function guestLocale(language: string | null | undefined): string {
  const lang = (language || 'en').split('-')[0];
  if (lang === 'hr') return 'hr-HR';
  if (lang === 'en') return 'en-GB';
  return lang;
}

/** Format a booking date in the guest's language; '' when missing/invalid. */
export function formatGuestDate(
  value: string | null | undefined,
  language: string | null | undefined,
  options: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short' },
): string {
  const d = parseLocalDate(value);
  if (!d) return '';
  try {
    return d.toLocaleDateString(guestLocale(language), options);
  } catch {
    return d.toLocaleDateString(undefined, options);
  }
}

/** "15:00:00" / "15:00" → "15:00"; '' when missing. */
export function formatGuestTime(time: string | null | undefined): string {
  if (!time) return '';
  const parts = time.split(':');
  return parts.length >= 2 ? `${parts[0].padStart(2, '0')}:${parts[1]}` : time;
}

/** Full years from a birth date ("YYYY-MM-DD", local) to today; null when invalid. */
export function ageInYears(birthDate: string | null | undefined, now: Date = new Date()): number | null {
  const d = parseLocalDate(birthDate);
  if (!d) return null;
  let age = now.getFullYear() - d.getFullYear();
  const hadBirthday = now.getMonth() > d.getMonth() || (now.getMonth() === d.getMonth() && now.getDate() >= d.getDate());
  if (!hadBirthday) age -= 1;
  return Math.max(0, age);
}

/** Localised weekday name (0 = Sunday … 6 = Saturday) in the guest's language. */
export function weekdayName(dayIndex: number, language: string | null | undefined): string {
  // 2024-01-07 was a Sunday.
  const d = new Date(2024, 0, 7 + (((dayIndex % 7) + 7) % 7));
  try {
    return d.toLocaleDateString(guestLocale(language), { weekday: 'long' });
  } catch {
    return d.toLocaleDateString(undefined, { weekday: 'long' });
  }
}
